import React from 'react';
import { isEmpty, noop, castArray, isNil, filter, isArray, map, merge, join, get, findIndex, isEqual, isBoolean, size, head } from 'lodash';

// import default view
import { typeahead as TypeaheadView } from './typeahead.view';
import { TypeaheadService } from 'services/typeahead';
import {
	SERVICE_RESPONSE_STATUS,
	PAGE_LEVEL_PRELOADER_TEMPLATES,
    DATA_TYPES,
    SIZES
} from 'constants/common';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { popover } from 'modules/popover';
import { ChoiceList } from 'modules/choice-list';
import { getLabel } from 'utils/getLabel';
import { isTabKey, isEscapeKey } from 'utils/keyChecker';

/**
 * @type {function}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @param {object} Component - A React component
 * @example
 * import { Typeahead } from 'modules/typeahead';
 *
 * // render
 * <Typeahead propName='propValue'/>
 */
export class Typeahead extends React.Component {
    constructor( props ) {
        super( props );

        // bind events and methods
        this.handleQueryChange = this.handleQueryChange.bind( this );
        this.handleButtonClick = this.handleButtonClick.bind( this );
        this.handleOptionSelection = this.handleOptionSelection.bind( this );
        this.closePopOverOnKeyUp = this.closePopOverOnKeyUp.bind( this );
        this.handleFocus = this.handleFocus.bind( this );
        this.handlePopoverClose = this.handlePopoverClose.bind( this );
        this.handlePopoverBelowSpace = this.handlePopoverBelowSpace.bind( this );

        // create service instance
        this.typeaheadService = new TypeaheadService();

        // search input element ref
        this.searchInputRef = React.createRef();
        this.selectButtonRef = React.createRef();

        // add event listener timeout
        this.timeout = null;
        // component state
        this.state = {
            searchQuery: '',
            isPopoverOpen: false,
            isFetching: false,
            options: this.props.options,
            selected: this.props.selected,
            selectedOptionTitle: '',
        };
    }



    
    handlePopoverBelowSpace() {
        this.handleElement('.ui-edit-health-info-allergies .field-value-search__typeahead .ui-search-input__icon');
        this.handleElement('.ui-edit-health-info-health-conditions .field-value-search__typeahead .ui-search-input__icon');

        this.handleElement('.ui-epost-allergies-and-health-conditions__allergies__section .field-value-search__typeahead .ui-search-input__icon');
        this.handleElement('.ui-epost-allergies-and-health-conditions__health__section .field-value-search__typeahead .ui-search-input__icon');
    }
    
    handleElement(selector) {
        const element = document.querySelector(selector);
        if (element) {
            if (element.classList.contains("ui-search-input__icon--close")) {
                const closestTypeahead = element.closest(".field-value-search__typeahead");
                if (closestTypeahead) {
                    closestTypeahead.classList.add('addBelowSpace');
                    this.toggleClassOrRemove(element);
                }
            } else {
                const closestTypeahead = element.closest(".field-value-search__typeahead");
                if (closestTypeahead) {
                    closestTypeahead.classList.remove('addBelowSpace');
                }
            }
        }
    }
    
    toggleClassOrRemove(element) {
        const isPopupExist = document.querySelector('.ui-popover--no-tooltip');
        if (isPopupExist) {
            element.closest(".field-value-search__typeahead").classList.add('addBelowSpace');
        } else {
            element.classList.remove("ui-search-input__icon--close");
        }
    }
    
    /*********************************************/

    /**
     * @desc Handle search input `query` change.
     */
    handleQueryChange( query ) {
        log.debug( 'Typeahead.handleQueryChange()' );

        // if `query` is empty, cleanup `options` and `selections`
        if( isEmpty( query ) ) {
            return this.setState( {
                isFetching: false,
                searchQuery: query,
                options: [],
				selected: [],
				selectedOptionsTitles: []
            }, () => {
                this.typeaheadService.cancel(); // cancel ongoing request
                this.handlePopoverClose( { force: true } );
                this.props.onQueryChange( this.state.searchQuery );
            } );
        }

        /*----------*/

        this.setState( {
            searchQuery: query,
            isFetching: true,
        }, () => {
            this.props.onQueryChange( this.state.searchQuery );
            this.fetchOptions( this.state.searchQuery );
        } );
    }

    /**
     * @desc Handle search input `query` change.
     */
    handleButtonClick() {
        log.debug( 'Typeahead.handleButtonClick()' );

        if( ! this.state.isPopoverOpen ) {
            this.handlePopoverOpen();
        }
    }

    /**
     * @desc Fetch `options` from the server based on current `searchQuery`.
     */
    async fetchOptions( searchQuery ) {
        log.debug( 'Typeahead.fetchOptions()' );

        // no-op if `url` is missing
        if( isEmpty( this.props.url ) ) {
            return;
        }

        /*----------*/

        // cancel ongoing request
        this.typeaheadService.cancel();

        // show page level preloader
        pageLevelPreloader.show( PAGE_LEVEL_PRELOADER_TEMPLATES.NANOBAR );

        // get status and service data
        const { status, data } = await this.typeaheadService.fetchOptions( {
            url: this.props.url,
            data: merge( this.props.requestPayload, {
                data: { searchQuery },
            } ),
            ignoreOptions: get( this.props, 'ignoreOptions', [] )
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.CANCELLED === status ) {

            return null; // ignore on request cancellations
         } else if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            
            if( isEmpty( this.state.searchQuery ) ) {
                this.setState( {
                    isFetching: false,
                    searchQuery: '',
                    options: [],
                    selected: [],
                    selectedOptionsTitles: []
                } );
            } else {
                this.setState( {
                    isFetching: false,
                    options: data,
                    selected: [], // reset selected option
                }, () => {
                    this.handlePopoverOpen( { force: true } );
                } );
            }

        } else {

            // show error dialog
			if ( ! isEmpty( data.description ) ) {
				errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
			} else {
				errorOverlay.open();
			}
        }
    }

    /**
     * @desc Open `popover` to display options.
     */
    handlePopoverOpen( { force = false } = {} ) {
        log.debug( 'Typeahead.handlePopoverOpen()' );
        this.handlePopoverBelowSpace();
        // if popover is already opened, update `options` only
        if( this.state.isPopoverOpen && ! force ) {
            return popover.update( {
                props: {
                    options: this.state.options,
                    selected: this.state.selected,
                }
            } );
        } else {

            // to prevent immediate closing of popover.
            if( isNil( this.timeout ) ) {
                this.timeout = setTimeout( () => {
                    document.addEventListener( 'keydown', this.closePopOverOnKeyUp );
                } );
            }
        }

        /*----------*/

        this.setState( {
            isPopoverOpen: true,
        }, () => {
            popover.close();
            // open new popover
            popover.open( {
                referenceElement: this.props.searchable ? this.searchInputRef.current : this.selectButtonRef.current,
                view: ChoiceList,
                tooltip: false,
                compact: isNil( this.props.popupWidth ),
                styles: {
                    width: this.props.popupWidth,
                },
                props: {
                    queryString: this.props.highlightQueryString ?  this.state.searchQuery: undefined,
                    allowFilter: this.props.allowFilter,
                    displaySelectAllOption: this.props.displaySelectAllOption,
                    type: this.props.type,
                    options: this.state.options,
                    selected: isBoolean( this.state.selected ) ? [ ...castArray( this.state.selected ), ...this.props.selected ] : [ ...this.state.selected, ...this.props.selected ],
                    onChange: this.handleOptionSelection,
                    selectionCriteria: this.props.selectionCriteria,
                    lowerPopoverZIndex: this.props.lowerPopoverZIndex
                },
                onClose: this.handlePopoverClose,
            } );
            
        } );
    }
    
    /**
     * @desc When popover is closed.
     */
    handlePopoverClose( { force = false } = {} ) {
        log.debug( 'Typeahead.handlePopoverClose()' );

        if( this.state.isPopoverOpen  || force ) {
            this.handlePopoverBelowSpace();
            this.setState( {
                isPopoverOpen: false
            }, () => {

                // close popover
                popover.close();
    
                // remove keydown event
                document.removeEventListener( 'keydown', this.closePopOverOnKeyUp );
                clearTimeout( this.timeout );
                this.timeout = null;
            } );
        }
    }

    /**
     * @desc Update `selected` options in the state and call the `onChange` callback function.
     */
    handleOptionSelection( selected = [] ) {
        log.debug( 'Typeahead.handleOptionSelection()' );

        // filter the selected options from all of the options
        // const selectedOption = filter( this.state.options, ( options ) => {
        //     return -1 !== findIndex( selected, { [ `${this.props.selectionCriteria}` ]: options[ this.props.selectionCriteria ] } ); //includes( selected, options[ this.props.selectionCriteria ] );
        // } );

        let selectedOption = [];

        if( isArray( selected ) ) {

            selectedOption = filter( this.state.options, ( options ) => {
                return -1 !== findIndex( selected, { [ `${this.props.selectionCriteria}` ]: options[ this.props.selectionCriteria ] } ); //includes( selected, options[ this.props.selectionCriteria ] );
            } );

        } else {
            selectedOption = filter( this.state.options, ( options ) => {
                return options[ `${this.props.selectionCriteria}` ] === selected; // -1 !== findIndex( selected, { [ `${this.props.selectionCriteria}` ]: options[ this.props.selectionCriteria ] } ); //includes( selected, options[ this.props.selectionCriteria ] );
            } );
        }

        // store their title into an array
        const selectedOptionsTitles = map( selectedOption, ( option ) => {
            return getLabel( option.title );
        }  );

        // update state
        if( this.props.ignoreQueryChangeOnSelect ) {
            this.setState( {
                selected,
            }, () => {
                this.props.onChange( {
                    options: this.state.options,
                    selected: castArray( this.state.selected )
                } );
            } );
        } else {
            
            this.setState( {
                selected,
                selectedOptionTitle: selectedOptionsTitles, //set the state with selected option's titles only
                searchQuery: join( selectedOptionsTitles, ',' )
            }, () => {
                this.props.onChange( {
                    options: this.state.options,
                    selected: castArray( this.state.selected )
                } );
            } );
        }

    }

    /**
     * @desc Close popover on keydown event.
     */
    closePopOverOnKeyUp( event ) {
        log.debug( 'Typeahead.closePopOverOnKeyUp()' );

        const key = get( event, 'code', get( event, 'key', '' ) );

        if( ( isEscapeKey( key )|| isTabKey( key ) ) ) {
            if( this.state.isPopoverOpen ) {
                this.handlePopoverClose();
            }
        }
    }

    /**
     * @desc Handle focus of input view
     */
    handleFocus() {
        log.debug( 'Typeahead.handleFocus()' );

        this.handlePopoverBelowSpace();

        if( ! this.props.searchable && ! this.state.isPopoverOpen ) {
            this.handlePopoverOpen(); // // to prevent immediate closing of popover
        }
    }

    UNSAFE_componentWillReceiveProps( newProps ) {
        log.debug( 'Typeahead.UNSAFE_componentWillReceiveProps()' );

        if ( ! isEqual( this.props.options, newProps.options ) ) {

            // if options need to be updated
            if ( this.props.updateOptionsOnChange ) {
                this.setState( { options: newProps.options, selected: newProps.selected, selectedOptionTitle: '', placeholder: newProps.placeholder, searchQuery: newProps.placeholder }, () => {
                    
                    // set default value or reset value based on options size
                    this.handleOptionSelection( ( 1 === size( this.state.options ) && this.props.updateValueOnSingleOption ) ? get( head( this.state.options ), 'value' ) : '' );
                } );
            }
        }

        // if values need to be updated from parent onChange
        if ( this.props.updateValuesOnChange ) {
            
            if ( !isEqual( this.state.selected, newProps.selected ) ) {

                this.setState( {
                    selected: newProps.selected,
                    searchQuery: newProps.placeholder,
                    selectedOptionTitle: ''
                } );
            }
        }

        // close popover on scroll, if need be
        if ( ! isEqual( this.props.closePopoverOnScrollTrigger, newProps.closePopoverOnScrollTrigger ) ) {
            this.handlePopoverClose();
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, clear resouces
     */
    componentWillUnmount() {
        if( this.timeout ) {
            clearTimeout( this.timeout );
            this.timeout = null;
        }
    }

    render() {
        return (
            <TypeaheadView
                { ...this.props }
                searchInputRef={ this.searchInputRef }
                selectButtonRef={ this.selectButtonRef }
                searchQuery={ this.state.searchQuery }
                selectedValTitle= { this.state.selectedOptionTitle }
                isFetching={ this.state.isFetching }
                onQueryChange={ this.handleQueryChange }
                placeholder= { this.props.placeholder }
                disabled= { this.props.disabled }
                onButtonClick={ this.handleButtonClick }
                onFocus={ this.handleFocus }
                activeblankdiv = {this.activeblankdiv}
                isBlankDivRquied = {this.isBlankDivRquied}

            />
        );
    }
}

// set display name
Typeahead.displayName = 'Typeahead';

// default props
Typeahead.defaultProps = {
    url: null,
    placeholder: undefined,
    allowFilter: false,
    displaySelectAllOption: false,
    popupWidth: undefined,
    type: DATA_TYPES.SINGLE_SELECT,
    searchable: true, // false if need to act as simple search box
    options: [], // if not searchable then send the options
    selected: [], // if any is option is selected
    requestPayload: {},
    onChange: noop,
    onQueryChange: noop,
    disabled: false,
    ignoreQueryChangeOnSelect: false,
    highlightQueryString: true,
    selectionCriteria: 'value',
    updateOptionsOnChange: false,
    activeblankdiv:false,
    isBlankDivRquied:false,
    lowerPopoverZIndex: false
};

// prop types
Typeahead.propTypes = {};
